<template>
  <div class="rate-quote-warnings">
    <template v-if="isChipMode">
      <RygenChip
        v-if="showWarning"
        v-tooltip="warningTooltip"
        :label="t('common.warn')"
        :count="warningCount"
        icon="pi pi-exclamation-triangle"
        :severity="Severity.WARNING"
      />
      <RygenChip
        v-if="showRateError"
        v-tooltip="rateErrorTooltip"
        :label="t('rateShop.unrated')"
        icon="pi pi-times-circle"
        :severity="Severity.DANGER"
      />
      <RygenChip
        v-if="showExpired"
        :label="t('common.expired')"
        icon="pi pi-exclamation-triangle"
        :severity="Severity.DANGER"
      />
    </template>

    <template v-else>
      <RygenButton
        v-if="showDoNotDispatch"
        mode="link"
        @click="emit('click', rateQuote)"
      >
        <RygenChip
          :label="t('carriers.doNotDispatch')"
          :severity="
            canOverrideCarrierDoNotDispatch ? Severity.WARNING : Severity.DANGER
          "
        />
      </RygenButton>

      <RygenButton
        v-if="showWarning"
        mode="link"
        @click="emit('click', rateQuote)"
      >
        <RygenChip
          :label="t('common.warn')"
          :count="warningCount"
          icon="pi pi-exclamation-triangle"
          :severity="Severity.WARNING"
        />
      </RygenButton>

      <template v-if="showRateError">
        <RygenButton mode="link" @click="emit('click', rateQuote)">
          <RygenChip
            :label="t('rateShop.unrated')"
            icon="pi pi-times-circle"
            :severity="Severity.DANGER"
          />
        </RygenButton>
        <span
          v-tooltip="rateErrorTooltip"
          class="rate-error-reason text-danger"
        >
          {{ rateQuote.warnings[0] }}
        </span>
      </template>

      <RygenButton
        v-if="showExpired"
        mode="link"
        @click="emit('click', rateQuote)"
      >
        <RygenChip
          :label="t('common.expired')"
          icon="pi pi-exclamation-triangle"
          :severity="Severity.DANGER"
        />
      </RygenButton>
    </template>
  </div>
</template>

<script lang="ts" setup>
import type { PropType } from 'vue';
import { computed, toRefs } from 'vue';
import { useI18n } from 'vue-i18n';

import { useTooltip } from '@rygen/rygen-ui/composables';
import { Severity } from '@rygen/rygen-ui/model';

import { useDoNotDispatch } from '@/composables';
import { RateQuote } from '@/model';

import RygenButton from '@rygen/rygen-ui/components/button/RygenButton.vue';
import RygenChip from '@rygen/rygen-ui/components/ui/RygenChip.vue';

type ComponentMode = 'BUTTON' | 'CHIP';

const props = defineProps({
  rateQuote: {
    type: RateQuote,
    default: () => new RateQuote(),
  },
  mode: {
    type: String as PropType<ComponentMode>,
    default: () => 'BUTTON',
  },
});
const { rateQuote, mode } = toRefs(props);
const emit = defineEmits<{
  (event: 'click', rateQuote: RateQuote): void;
}>();

const { canOverrideCarrierDoNotDispatch, isDoNotDispatchEnabled } =
  useDoNotDispatch();
const { t } = useI18n();
const vTooltip = useTooltip();

const isChipMode = computed(() => mode.value === 'CHIP');

const showDoNotDispatch = computed(
  () => isDoNotDispatchEnabled && rateQuote.value.doNotDispatch,
);

const showWarning = computed(
  () =>
    rateQuote.value.rateStatus === 'WARNING' ||
    (rateQuote.value.rateStatus !== 'ERROR' &&
      rateQuote.value.carrierComplianceRuleWarnings.length),
);

const showRateError = computed(() => rateQuote.value.rateStatus === 'ERROR');

const rateErrorTooltip = computed(() => rateQuote.value.warnings.join('\n'));

const showExpired = computed(() => isExpired.value);

const isExpired = computed(
  () =>
    !!rateQuote.value.quoteExpiration &&
    rateQuote.value.quoteExpiration < new Date(),
);

const warningCount = computed(() => {
  const warningCount =
    rateQuote.value.warnings.length +
    rateQuote.value.carrierComplianceRuleWarnings.length;

  return warningCount > 1 ? warningCount : undefined;
});

const warningTooltip = computed(() => {
  const combinedWarnings = [
    ...rateQuote.value.warnings,
    ...rateQuote.value.carrierComplianceRuleWarnings,
  ];

  return combinedWarnings.map((warning) => `- ${warning}`).join('\n');
});
</script>

<style>
.rate-quote-warnings {
  display: flex;
  gap: var(--sp-3);
  flex-wrap: wrap;

  &:empty {
    display: none;
  }

  .rate-error-reason {
    max-width: 28ch;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
}
</style>
